import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, 
  FileText, 
  AlertTriangle, 
  Users, 
  Truck, 
  PieChart, 
  Star, 
  Phone,
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  BarChart3,
  TrendingDown,
  Award,
  Package
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const Dashboard = () => {
  const [kpi, setKpi] = useState(null);
  const [enhancedKpi, setEnhancedKpi] = useState(null);
  const [operationalKpi, setOperationalKpi] = useState(null);
  const [qualityKpi, setQualityKpi] = useState(null);
  const [growthKpi, setGrowthKpi] = useState(null);
  const [marchandisesKpi, setMarchandisesKpi] = useState(null);
  const [fiabiliteKpi, setFiabiliteKpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('vue-ensemble');
  const [periodDays, setPeriodDays] = useState(30);

  useEffect(() => {
    fetchKPI();
  }, []);

  useEffect(() => {
    if (activeTab === 'operationnel') {
      fetchOperationalKPI(periodDays);
    } else if (activeTab === 'qualite') {
      fetchQualityKPI(periodDays);
    } else if (activeTab === 'croissance') {
      fetchGrowthKPI(periodDays);
    } else if (activeTab === 'fiabilite') {
      fetchFiabiliteKPI();
      fetchMarchandisesKPI(periodDays);
    }
  }, [activeTab, periodDays]);

  const fetchKPI = async () => {
    try {
      const response = await api.get('/kpi/dashboard-overview');
      setKpi(response.data);
      setEnhancedKpi(response.data);
    } catch (error) {
      console.error('Erreur KPI:', error);
      toast.error('Erreur lors du chargement des KPI');
    } finally {
      setLoading(false);
    }
  };

  const fetchOperationalKPI = async (days) => {
    try {
      const response = await api.get(`/kpi/operational-performance?period_days=${days}`);
      setOperationalKpi(response.data);
    } catch (error) {
      console.error('Erreur KPI opérationnels:', error);
      toast.error('Erreur lors du chargement des KPI opérationnels');
    }
  };

  const fetchQualityKPI = async (days) => {
    try {
      const response = await api.get(`/kpi/quality-compliance?period_days=${days}`);
      setQualityKpi(response.data);
    } catch (error) {
      console.error('Erreur KPI qualité:', error);
      toast.error('Erreur lors du chargement des KPI qualité');
    }
  };

  const fetchGrowthKPI = async (days) => {
    try {
      const response = await api.get(`/kpi/growth-adoption?period_days=${days}`);
      setGrowthKpi(response.data);
    } catch (error) {
      console.error('Erreur KPI croissance:', error);
      toast.error('Erreur lors du chargement des KPI croissance');
    }
  };

  const fetchMarchandisesKPI = async (days) => {
    try {
      const response = await api.get(`/kpi/marchandises?period_days=${days}`);
      setMarchandisesKpi(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des KPI marchandises');
    }
  };

  const fetchFiabiliteKPI = async () => {
    try {
      const response = await api.get('/kpi/fiabilite');
      setFiabiliteKpi(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des KPI fiabilité');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Composant pour afficher un KPI avec statut
  const KPICard = ({ title, value, target, status, icon: Icon, color, trend, subtitle }) => {
    const getStatusColor = (status) => {
      if (status === 'ok') return 'text-green-600';
      if (status === 'below' || status === 'above') return 'text-red-600';
      if (status === 'no_data') return 'text-gray-400';
      return 'text-gray-600';
    };

    const getStatusIcon = (status) => {
      if (status === 'ok') return <CheckCircle className="h-4 w-4" />;
      if (status === 'below' || status === 'above') return <XCircle className="h-4 w-4" />;
      return null;
    };

    return (
      <Card className={`border-0 shadow-md ${color}`}>
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700 mb-2">{title}</p>
              <p className="text-3xl font-bold text-gray-900">{value}</p>
              {subtitle && (
                <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
              )}
              {target && (
                <div className={`flex items-center gap-1 mt-2 text-sm ${getStatusColor(status)}`}>
                  {getStatusIcon(status)}
                  <span>Objectif: {target}</span>
                </div>
              )}
            </div>
            <div className="p-3 rounded-xl bg-white bg-opacity-50">
              <Icon className="h-6 w-6 text-gray-700" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const stats = [
    {
      title: 'Total Opérations',
      value: kpi?.total_operations || 0,
      icon: Package,
      color: 'bg-emerald-100 text-emerald-700',
      bgColor: 'bg-gradient-to-br from-emerald-50 to-teal-50',
    },
    {
      title: 'Opérations Terminées',
      value: kpi?.operations_terminees || 0,
      icon: CheckCircle,
      color: 'bg-green-100 text-green-700',
      bgColor: 'bg-gradient-to-br from-green-50 to-emerald-50',
    },
    {
      title: 'En Cours',
      value: kpi?.operations_en_cours || 0,
      icon: Clock,
      color: 'bg-amber-100 text-amber-700',
      bgColor: 'bg-gradient-to-br from-amber-50 to-orange-50',
    },
    {
      title: 'Chauffeurs',
      value: kpi?.total_chauffeurs || kpi?.chauffeurs_actifs || 0,
      subtitle: kpi?.chauffeurs_actifs ? `${kpi.chauffeurs_actifs} actifs` : null,
      icon: Users,
      color: 'bg-blue-100 text-blue-700',
      bgColor: 'bg-gradient-to-br from-blue-50 to-indigo-50',
    },
    {
      title: 'Véhicules',
      value: kpi?.total_vehicules || kpi?.vehicules_actifs || 0,
      subtitle: kpi?.vehicules_actifs ? `${kpi.vehicules_actifs} actifs` : null,
      icon: Truck,
      color: 'bg-purple-100 text-purple-700',
      bgColor: 'bg-gradient-to-br from-purple-50 to-pink-50',
    },
  ];

  // Nouveaux KPI enrichis
  const enrichedStats = [
    {
      title: 'Taux de Complétion',
      value: `${kpi?.taux_completion || 0}%`,
      subtitle: `${kpi?.operations_terminees || 0}/${kpi?.total_operations || 0} opérations`,
      icon: Award,
      color: 'bg-amber-100 text-amber-700',
      bgColor: 'bg-gradient-to-br from-amber-50 to-yellow-50',
    },
    {
      title: 'Clients',
      value: kpi?.total_clients || kpi?.clients_actifs || 0,
      subtitle: kpi?.clients_actifs ? `${kpi.clients_actifs} avec opérations` : null,
      icon: Users,
      color: 'bg-green-100 text-green-700',
      bgColor: 'bg-gradient-to-br from-green-50 to-emerald-50',
    },
    {
      title: 'Nouvelles Demandes',
      value: kpi?.operations_nouveau || 0,
      subtitle: `À traiter`,
      icon: AlertTriangle,
      color: 'bg-teal-100 text-teal-700',
      bgColor: 'bg-gradient-to-br from-teal-50 to-cyan-50',
    },
  ];

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Tableau de bord</h1>
        <p className="text-gray-600 mt-2">Vue d'ensemble de l'activité Baobab Connect</p>
      </div>

      {/* Système d'onglets */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-5 gap-2 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger 
            value="vue-ensemble" 
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Vue d'ensemble
          </TabsTrigger>
          <TabsTrigger 
            value="operationnel" 
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Activity className="h-4 w-4 mr-2" />
            Opérationnel
          </TabsTrigger>
          <TabsTrigger 
            value="qualite" 
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Award className="h-4 w-4 mr-2" />
            Qualité
          </TabsTrigger>
          <TabsTrigger 
            value="croissance" 
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            Croissance
          </TabsTrigger>
          <TabsTrigger 
            value="fiabilite" 
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <FileText className="h-4 w-4 mr-2" />
            Fiabilité
          </TabsTrigger>
        </TabsList>

        {/* Onglet Vue d'ensemble */}
        <TabsContent value="vue-ensemble" className="space-y-6 mt-6">
          <div>

      {/* KPI Cards - Indicateurs Classiques */}
      <div>
        <h2 className="text-xl font-semibold text-gray-800 mb-4">📊 Indicateurs Principaux</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className={`border-0 shadow-md ${stat.bgColor} animate-fade-in`} style={{ animationDelay: `${index * 0.1}s` }}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">{stat.title}</p>
                      <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                    </div>
                    <div className={`p-3 rounded-xl ${stat.color}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Nouveaux KPI Enrichis - Chauffeurs */}
      <div>
        <h2 className="text-xl font-semibold text-gray-800 mb-4">👥 Suivi Chauffeurs</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {enrichedStats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className={`border-0 shadow-md ${stat.bgColor} animate-fade-in`} style={{ animationDelay: `${(index + 5) * 0.1}s` }}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">{stat.title}</p>
                      <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                      {stat.subtitle && (
                        <p className="text-xs text-gray-600">{stat.subtitle}</p>
                      )}
                    </div>
                    <div className={`p-3 rounded-xl ${stat.color}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Phase 2 Enhanced KPIs - Last 90 Days */}
      <div>
        <h2 className="text-xl font-semibold text-gray-800 mb-4">📈 Statistiques Avancées (90 derniers jours)</h2>
        
        {/* Row 1: Operations Stats and Chauffeur of Week */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Operations Statistics */}
          <Card className="shadow-md border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-emerald-600" />
                Statistiques Opérations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Total</p>
                  <p className="text-3xl font-bold text-gray-900">{enhancedKpi?.operations_stats?.total || 0}</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">En cours</p>
                  <p className="text-3xl font-bold text-blue-600">{enhancedKpi?.operations_stats?.en_cours || 0}</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Terminées</p>
                  <p className="text-3xl font-bold text-green-600">{enhancedKpi?.operations_stats?.terminees || 0}</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Annulées</p>
                  <p className="text-3xl font-bold text-red-600">{enhancedKpi?.operations_stats?.annulees || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Chauffeur of the Week */}
          <Card className="shadow-md border-0 bg-gradient-to-br from-amber-50 to-yellow-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5 text-amber-600" />
                🏆 Chauffeur de la Semaine
              </CardTitle>
            </CardHeader>
            <CardContent>
              {enhancedKpi?.chauffeur_of_week ? (
                <div className="text-center py-4">
                  <p className="text-2xl font-bold text-gray-900 mb-2">{enhancedKpi.chauffeur_of_week.nom}</p>
                  <p className="text-4xl font-bold text-amber-600">{enhancedKpi.chauffeur_of_week.count}</p>
                  <p className="text-sm text-gray-600 mt-1">opérations cette semaine</p>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Row 2: Top Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top 5 Destinations */}
          <Card className="shadow-md border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                🎯 Top 5 Destinations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {enhancedKpi?.top_destinations?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded">#{index + 1}</span>
                      <span className="text-sm text-gray-700">{item.destination}</span>
                    </div>
                    <span className="text-lg font-bold text-emerald-600">{item.count}</span>
                  </div>
                ))}
                {(!enhancedKpi?.top_destinations || enhancedKpi.top_destinations.length === 0) && (
                  <p className="text-gray-500 text-center py-4 text-sm">Aucune donnée disponible</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top 5 Chauffeurs */}
          <Card className="shadow-md border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4 text-emerald-600" />
                Top 5 Chauffeurs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {enhancedKpi?.top_chauffeurs?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded">#{index + 1}</span>
                      <span className="text-sm text-gray-700">{item.nom}</span>
                    </div>
                    <span className="text-lg font-bold text-emerald-600">{item.count}</span>
                  </div>
                ))}
                {(!enhancedKpi?.top_chauffeurs || enhancedKpi.top_chauffeurs.length === 0) && (
                  <p className="text-gray-500 text-center py-4 text-sm">Aucune donnée disponible</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top 5 Incidents */}
          <Card className="shadow-md border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                Top 5 Incidents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {enhancedKpi?.top_incidents?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded">#{index + 1}</span>
                      <span className="text-sm text-gray-700">{item.type}</span>
                    </div>
                    <span className="text-lg font-bold text-red-600">{item.count}</span>
                  </div>
                ))}
                {(!enhancedKpi?.top_incidents || enhancedKpi.top_incidents.length === 0) && (
                  <p className="text-gray-500 text-center py-4 text-sm">Aucune donnée disponible</p>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-600 text-center">
                  Total incidents: <span className="font-bold">{enhancedKpi?.total_incidents || 0}</span>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Repartition Interland/Intraland */}
        <Card className="shadow-md border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5 text-emerald-600" />
              Répartition Interland / Intraland
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(kpi?.repartition_interland || {}).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-4 w-4 rounded-full ${key === 'Interland' ? 'bg-emerald-500' : 'bg-teal-500'}`}></div>
                    <span className="text-gray-700 font-medium">{key}</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Types Camions */}
        <Card className="shadow-md border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-emerald-600" />
              Top 5 Types de camions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {kpi?.top_types_camions?.map((item, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <span className="text-gray-700 font-medium">{item.type}</span>
                  <span className="text-xl font-bold text-emerald-600">{item.count}</span>
                </div>
              ))}
              {(!kpi?.top_types_camions || kpi.top_types_camions.length === 0) && (
                <p className="text-gray-500 text-center py-4">Aucune donnée disponible</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
          </div>
        </TabsContent>

        {/* ============================================
            ONGLET OPÉRATIONNEL - NOUVEAUX KPI
        ============================================ */}
        <TabsContent value="operationnel" className="space-y-6 mt-6">
          {/* Sélecteur de période */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gray-50 p-4 rounded-lg">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">KPI Opérationnels</h2>
              <p className="text-sm text-gray-600 mt-1">Performance des opérations de transport</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant={periodDays === 7 ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriodDays(7)}
              >
                7 jours
              </Button>
              <Button
                variant={periodDays === 30 ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriodDays(30)}
              >
                30 jours
              </Button>
              <Button
                variant={periodDays === 90 ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriodDays(90)}
              >
                90 jours
              </Button>
            </div>
          </div>

          {operationalKpi ? (
            <>
              {/* KPI Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Opérations Période */}
                <KPICard
                  title={`Opérations (${operationalKpi.period_days || periodDays}j)`}
                  value={operationalKpi.operations_period || 0}
                  icon={Package}
                  color="bg-gradient-to-br from-blue-50 to-indigo-50"
                />

                {/* Moyenne par Jour */}
                <KPICard
                  title="Moyenne / Jour"
                  value={`${operationalKpi.avg_operations_jour || 0}`}
                  icon={Activity}
                  color="bg-gradient-to-br from-green-50 to-emerald-50"
                />

                {/* Taux Utilisation Chauffeurs */}
                <KPICard
                  title="Utilisation Chauffeurs"
                  value={`${operationalKpi.taux_utilisation_chauffeurs || 0}%`}
                  icon={Users}
                  color="bg-gradient-to-br from-purple-50 to-pink-50"
                />

                {/* Durée Moyenne */}
                <KPICard
                  title="Durée Moyenne Opération"
                  value={`${operationalKpi.avg_duree_operation_jours || 0}j`}
                  icon={Clock}
                  color="bg-gradient-to-br from-amber-50 to-yellow-50"
                />
              </div>

              {/* Statistiques Détaillées */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Métriques Performance */}
                <Card className="shadow-md border-0">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <BarChart3 className="h-5 w-5 text-emerald-600" />
                      Métriques Performance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-700">Opérations ({operationalKpi.period_days || periodDays}j)</span>
                          <span className="text-xs text-gray-500">Total période</span>
                        </div>
                        <span className="text-2xl font-bold text-blue-600">
                          {operationalKpi.operations_period || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-700">Moyenne / Jour</span>
                          <span className="text-xs text-gray-500">Activité quotidienne</span>
                        </div>
                        <span className="text-2xl font-bold text-green-600">
                          {operationalKpi.avg_operations_jour || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-700">Durée Moyenne</span>
                          <span className="text-xs text-gray-500">En jours</span>
                        </div>
                        <span className="text-2xl font-bold text-purple-600">
                          {operationalKpi.avg_duree_operation_jours || 0}j
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Conformité Documentaire */}
                <Card className="shadow-md border-0 bg-gradient-to-br from-amber-50 to-yellow-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileText className="h-5 w-5 text-amber-600" />
                      Conformité Documentaire
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-6">
                      <div className={`text-5xl font-bold mb-2 ${
                        operationalKpi.taux_conformite_documentaire >= 95 ? 'text-green-600' : 'text-amber-600'
                      }`}>
                        {operationalKpi.taux_conformite_documentaire || 0}%
                      </div>
                      <p className="text-sm text-gray-600">Documents complets</p>
                      <div className={`flex items-center justify-center gap-1 mt-3 text-sm ${
                        operationalKpi.taux_conformite_documentaire >= 95 ? 'text-green-600' : 'text-amber-600'
                      }`}>
                        {operationalKpi.taux_conformite_documentaire >= 95 ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          <AlertTriangle className="h-4 w-4" />
                        )}
                        <span>Objectif: ≥ 95%</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Utilisation Chauffeurs */}
                <Card className="shadow-md border-0">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Users className="h-5 w-5 text-purple-600" />
                      Utilisation Chauffeurs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-6">
                      <div className="text-5xl font-bold text-purple-600 mb-2">
                        {operationalKpi.taux_utilisation_chauffeurs || 0}%
                      </div>
                      <p className="text-sm text-gray-600 mb-4">Chauffeurs actifs</p>
                      <p className="text-xs text-gray-500">
                        Pourcentage de chauffeurs avec opérations sur 30j
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Informations de période */}
              <Card className="border-0 bg-gray-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>📅 Période d'analyse : <strong>{operationalKpi.period_days || periodDays} derniers jours</strong></span>
                    <span>📊 Dernière mise à jour : {new Date().toLocaleString('fr-FR')}</span>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Chargement des KPI opérationnels...</p>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ============================================
            ONGLET QUALITÉ & SATISFACTION
        ============================================ */}
        <TabsContent value="qualite" className="space-y-6 mt-6">
          {/* Sélecteur de période */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gray-50 p-4 rounded-lg">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">KPI Qualité & Satisfaction</h2>
              <p className="text-sm text-gray-600 mt-1">Fiabilité du service et satisfaction client</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant={periodDays === 7 ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriodDays(7)}
              >
                7 jours
              </Button>
              <Button
                variant={periodDays === 30 ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriodDays(30)}
              >
                30 jours
              </Button>
              <Button
                variant={periodDays === 90 ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriodDays(90)}
              >
                90 jours
              </Button>
            </div>
          </div>

          {qualityKpi ? (
            <>
              {/* KPI Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Conformité Documentaire */}
                <KPICard
                  title="Conformité Documentaire"
                  value={`${qualityKpi.taux_conformite_documentaire || 0}%`}
                  icon={CheckCircle}
                  color="bg-gradient-to-br from-green-50 to-emerald-50"
                />

                {/* Poids Renseigné */}
                <KPICard
                  title="Poids Renseigné"
                  value={`${qualityKpi.taux_poids_renseigne || 0}%`}
                  icon={Package}
                  color="bg-gradient-to-br from-blue-50 to-indigo-50"
                />

                {/* Volume Renseigné */}
                <KPICard
                  title="Volume Renseigné"
                  value={`${qualityKpi.taux_volume_renseigne || 0}%`}
                  icon={Package}
                  color="bg-gradient-to-br from-purple-50 to-pink-50"
                />

                {/* Taux Réclamations */}
                <KPICard
                  title="Taux Réclamations"
                  value={`${qualityKpi.taux_reclamations || 0}%`}
                  icon={AlertTriangle}
                  color="bg-gradient-to-br from-amber-50 to-orange-50"
                />
              </div>

              {/* Statistiques Détaillées Row 1 */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Complétude Données */}
                <Card className="shadow-md border-0">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileText className="h-5 w-5 text-blue-600" />
                      Complétude des Données
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Documents</span>
                        <span className="text-2xl font-bold text-green-600">
                          {qualityKpi.taux_conformite_documentaire || 0}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Poids</span>
                        <span className="text-2xl font-bold text-blue-600">
                          {qualityKpi.taux_poids_renseigne || 0}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Volume</span>
                        <span className="text-2xl font-bold text-purple-600">
                          {qualityKpi.taux_volume_renseigne || 0}%
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Réclamations Détail */}
                <Card className="shadow-md border-0 bg-gradient-to-br from-amber-50 to-orange-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <AlertTriangle className="h-5 w-5 text-amber-600" />
                      Réclamations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-4">
                      <div className="text-5xl font-bold text-amber-600 mb-2">
                        {qualityKpi.total_reclamations || 0}
                      </div>
                      <p className="text-sm text-gray-600 mb-4">Total réclamations</p>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm p-2 bg-white rounded">
                          <span className="text-gray-600">Taux réclamations</span>
                          <span className="font-bold text-amber-600">{qualityKpi.taux_reclamations || 0}%</span>
                        </div>
                        <div className="flex items-center justify-between text-sm p-2 bg-white rounded">
                          <span className="text-gray-600">Taux résolution</span>
                          <span className="font-bold text-green-600">{qualityKpi.taux_resolution_reclamations || 0}%</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Objectifs Qualité */}
                <Card className="shadow-md border-0">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Award className="h-5 w-5 text-purple-600" />
                      Objectifs Qualité
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-600">Conformité doc</span>
                        <span className={`font-bold ${qualityKpi.taux_conformite_documentaire >= 95 ? 'text-green-600' : 'text-red-600'}`}>
                          {qualityKpi.taux_conformite_documentaire >= 95 ? '✓' : '✗'} Objectif: ≥95%
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-600">Poids renseigné</span>
                        <span className={`font-bold ${qualityKpi.taux_poids_renseigne >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
                          {qualityKpi.taux_poids_renseigne >= 80 ? '✓' : '○'} Objectif: ≥80%
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-600">Réclamations</span>
                        <span className={`font-bold ${qualityKpi.taux_reclamations <= 5 ? 'text-green-600' : 'text-red-600'}`}>
                          {qualityKpi.taux_reclamations <= 5 ? '✓' : '✗'} Objectif: ≤5%
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Incidents par Type et Gravité */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Incidents par Type */}
                <Card className="shadow-md border-0">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <BarChart3 className="h-5 w-5 text-red-600" />
                      Incidents par Type
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {qualityKpi.incidents_par_type && qualityKpi.incidents_par_type.length > 0 ? (
                      <div className="space-y-3">
                        {qualityKpi.incidents_par_type.map((item, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="flex items-center gap-3">
                              <div className={`h-3 w-3 rounded-full ${
                                index === 0 ? 'bg-red-500' : 
                                index === 1 ? 'bg-orange-500' : 
                                index === 2 ? 'bg-yellow-500' : 'bg-gray-400'
                              }`}></div>
                              <span className="text-sm font-medium text-gray-700">{item.type}</span>
                            </div>
                            <span className="text-lg font-bold text-red-600">{item.count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8 text-sm">Aucun incident</p>
                    )}
                  </CardContent>
                </Card>

                {/* Incidents par Gravité */}
                <Card className="shadow-md border-0">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <TrendingUp className="h-5 w-5 text-orange-600" />
                      Incidents par Gravité
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {qualityKpi.incidents_par_gravite && qualityKpi.incidents_par_gravite.length > 0 ? (
                      <div className="space-y-3">
                        {qualityKpi.incidents_par_gravite.map((item, index) => (
                          <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${
                            item.gravite === 'Critique' ? 'bg-red-50' :
                            item.gravite === 'Élevée' ? 'bg-orange-50' :
                            item.gravite === 'Moyenne' ? 'bg-yellow-50' : 'bg-green-50'
                          }`}>
                            <span className="text-sm font-medium text-gray-700">{item.gravite}</span>
                            <span className={`text-lg font-bold ${
                              item.gravite === 'Critique' ? 'text-red-600' :
                              item.gravite === 'Élevée' ? 'text-orange-600' :
                              item.gravite === 'Moyenne' ? 'text-yellow-600' : 'text-green-600'
                            }`}>{item.count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8 text-sm">Aucun incident</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Footer Info */}
              <Card className="border-0 bg-gray-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>📅 Période d'analyse : <strong>{qualityKpi.period_days} jours</strong></span>
                    <span>📊 Dernière mise à jour : {new Date().toLocaleString('fr-FR')}</span>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Chargement des KPI qualité...</p>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ============================================
            ONGLET FIABILITÉ & MARCHANDISES
        ============================================ */}
        <TabsContent value="fiabilite" className="space-y-6 mt-6">
          {/* Sélecteur de période pour marchandises */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gray-50 p-4 rounded-lg">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Fiabilité & Marchandises</h2>
              <p className="text-sm text-gray-600 mt-1">Qualité de la base et analyse des marchandises</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant={periodDays === 7 ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriodDays(7)}
              >
                7 jours
              </Button>
              <Button
                variant={periodDays === 30 ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriodDays(30)}
              >
                30 jours
              </Button>
              <Button
                variant={periodDays === 90 ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriodDays(90)}
              >
                90 jours
              </Button>
            </div>
          </div>

          {fiabiliteKpi && marchandisesKpi ? (
            <>
              {/* Indice Global de Fiabilité - Hero Section */}
              <Card className="shadow-lg border-0 bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                <CardContent className="p-8">
                  <div className="text-center">
                    <h3 className="text-lg font-medium mb-4 opacity-90">Indice Global de Fiabilité</h3>
                    <div className={`text-7xl font-bold mb-4 ${
                      fiabiliteKpi.indice_fiabilite.score >= 85 ? 'text-white' :
                      fiabiliteKpi.indice_fiabilite.score >= 70 ? 'text-yellow-300' : 'text-red-300'
                    }`}>
                      {fiabiliteKpi.indice_fiabilite.score}
                      <span className="text-3xl">/100</span>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-sm">
                      {fiabiliteKpi.indice_fiabilite.status === 'ok' ? (
                        <>
                          <CheckCircle className="h-5 w-5" />
                          <span>Excellente qualité de base</span>
                        </>
                      ) : fiabiliteKpi.indice_fiabilite.status === 'warning' ? (
                        <>
                          <AlertTriangle className="h-5 w-5" />
                          <span>Qualité acceptable - Améliorations nécessaires</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-5 w-5" />
                          <span>Qualité critique - Action urgente requise</span>
                        </>
                      )}
                    </div>
                    <p className="text-xs opacity-75 mt-3">Objectif : ≥ {fiabiliteKpi.indice_fiabilite.target}/100</p>
                  </div>
                </CardContent>
              </Card>

              {/* KPI Doublons - Section Critique */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">🚨 Détection de Doublons</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Doublons Complets */}
                  <Card className={`shadow-md border-0 ${
                    fiabiliteKpi.doublons_complets.status === 'ok' ? 'bg-gradient-to-br from-green-50 to-emerald-50' :
                    fiabiliteKpi.doublons_complets.status === 'warning' ? 'bg-gradient-to-br from-yellow-50 to-amber-50' :
                    'bg-gradient-to-br from-red-50 to-pink-50'
                  }`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-700 mb-2">Doublons Complets</p>
                          <p className="text-3xl font-bold text-gray-900">{fiabiliteKpi.doublons_complets.nombre}</p>
                          <p className="text-sm text-gray-600 mt-1">{fiabiliteKpi.doublons_complets.taux}%</p>
                        </div>
                        <div className={`p-3 rounded-xl ${
                          fiabiliteKpi.doublons_complets.status === 'ok' ? 'bg-green-100' :
                          fiabiliteKpi.doublons_complets.status === 'warning' ? 'bg-yellow-100' : 'bg-red-100'
                        }`}>
                          <Users className={`h-6 w-6 ${
                            fiabiliteKpi.doublons_complets.status === 'ok' ? 'text-green-700' :
                            fiabiliteKpi.doublons_complets.status === 'warning' ? 'text-yellow-700' : 'text-red-700'
                          }`} />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">Objectif : &lt;{fiabiliteKpi.doublons_complets.target}%</p>
                    </CardContent>
                  </Card>

                  {/* Doublons Téléphone */}
                  <Card className={`shadow-md border-0 ${
                    fiabiliteKpi.doublons_telephone.status === 'ok' ? 'bg-gradient-to-br from-green-50 to-emerald-50' :
                    fiabiliteKpi.doublons_telephone.status === 'warning' ? 'bg-gradient-to-br from-yellow-50 to-amber-50' :
                    'bg-gradient-to-br from-red-50 to-pink-50'
                  }`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-700 mb-2">Doublons Téléphone</p>
                          <p className="text-3xl font-bold text-gray-900">{fiabiliteKpi.doublons_telephone.nombre}</p>
                          <p className="text-sm text-gray-600 mt-1">{fiabiliteKpi.doublons_telephone.taux}%</p>
                        </div>
                        <div className={`p-3 rounded-xl ${
                          fiabiliteKpi.doublons_telephone.status === 'ok' ? 'bg-green-100' :
                          fiabiliteKpi.doublons_telephone.status === 'warning' ? 'bg-yellow-100' : 'bg-red-100'
                        }`}>
                          <Phone className={`h-6 w-6 ${
                            fiabiliteKpi.doublons_telephone.status === 'ok' ? 'text-green-700' :
                            fiabiliteKpi.doublons_telephone.status === 'warning' ? 'text-yellow-700' : 'text-red-700'
                          }`} />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">Objectif : {fiabiliteKpi.doublons_telephone.target}%</p>
                    </CardContent>
                  </Card>

                  {/* Doublons CNI - FRAUDE */}
                  <Card className={`shadow-md border-0 ${
                    fiabiliteKpi.doublons_cni.status === 'ok' ? 'bg-gradient-to-br from-green-50 to-emerald-50' :
                    'bg-gradient-to-br from-red-100 to-red-200'
                  }`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-700 mb-2">Doublons CNI 🚨</p>
                          <p className="text-3xl font-bold text-red-700">{fiabiliteKpi.doublons_cni.nombre}</p>
                          <p className="text-sm text-gray-600 mt-1">{fiabiliteKpi.doublons_cni.taux}%</p>
                        </div>
                        <div className="p-3 rounded-xl bg-red-200">
                          <AlertTriangle className="h-6 w-6 text-red-700" />
                        </div>
                      </div>
                      <p className="text-xs text-red-700 font-medium">⚠️ FRAUDE POTENTIELLE</p>
                    </CardContent>
                  </Card>

                  {/* Doublons Immatriculation */}
                  <Card className={`shadow-md border-0 ${
                    fiabiliteKpi.doublons_immatriculation.status === 'ok' ? 'bg-gradient-to-br from-green-50 to-emerald-50' :
                    fiabiliteKpi.doublons_immatriculation.status === 'warning' ? 'bg-gradient-to-br from-yellow-50 to-amber-50' :
                    'bg-gradient-to-br from-red-50 to-pink-50'
                  }`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-700 mb-2">Doublons Immat.</p>
                          <p className="text-3xl font-bold text-gray-900">{fiabiliteKpi.doublons_immatriculation.nombre}</p>
                          <p className="text-sm text-gray-600 mt-1">{fiabiliteKpi.doublons_immatriculation.taux}%</p>
                        </div>
                        <div className={`p-3 rounded-xl ${
                          fiabiliteKpi.doublons_immatriculation.status === 'ok' ? 'bg-green-100' :
                          fiabiliteKpi.doublons_immatriculation.status === 'warning' ? 'bg-yellow-100' : 'bg-red-100'
                        }`}>
                          <Truck className={`h-6 w-6 ${
                            fiabiliteKpi.doublons_immatriculation.status === 'ok' ? 'text-green-700' :
                            fiabiliteKpi.doublons_immatriculation.status === 'warning' ? 'text-yellow-700' : 'text-red-700'
                          }`} />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">Objectif : &lt;{fiabiliteKpi.doublons_immatriculation.target}%</p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Qualité des Données */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="shadow-md border-0">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileText className="h-5 w-5 text-orange-600" />
                      Qualité des Données
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className={`p-4 rounded-lg ${
                        fiabiliteKpi.champs_manquants.status === 'ok' ? 'bg-green-50' :
                        fiabiliteKpi.champs_manquants.status === 'warning' ? 'bg-yellow-50' : 'bg-red-50'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Champs Manquants</span>
                          <span className={`text-2xl font-bold ${
                            fiabiliteKpi.champs_manquants.status === 'ok' ? 'text-green-600' :
                            fiabiliteKpi.champs_manquants.status === 'warning' ? 'text-yellow-600' : 'text-red-600'
                          }`}>{fiabiliteKpi.champs_manquants.taux}%</span>
                        </div>
                        <p className="text-xs text-gray-500">{fiabiliteKpi.champs_manquants.nombre} champs vides</p>
                      </div>
                      <div className={`p-4 rounded-lg ${
                        fiabiliteKpi.documents_expires.status === 'ok' ? 'bg-green-50' :
                        fiabiliteKpi.documents_expires.status === 'warning' ? 'bg-yellow-50' : 'bg-red-50'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Documents Expirés</span>
                          <span className={`text-2xl font-bold ${
                            fiabiliteKpi.documents_expires.status === 'ok' ? 'text-green-600' :
                            fiabiliteKpi.documents_expires.status === 'warning' ? 'text-yellow-600' : 'text-red-600'
                          }`}>{fiabiliteKpi.documents_expires.taux}%</span>
                        </div>
                        <p className="text-xs text-gray-500">{fiabiliteKpi.documents_expires.nombre} documents périmés</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-md border-0">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <AlertTriangle className="h-5 w-5 text-purple-600" />
                      Incohérences
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className={`p-4 rounded-lg ${
                        fiabiliteKpi.mismatch_partenaire.status === 'ok' ? 'bg-green-50' : 'bg-yellow-50'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Sans Partenaire</span>
                          <span className="text-2xl font-bold text-purple-600">{fiabiliteKpi.mismatch_partenaire.nombre}</span>
                        </div>
                        <p className="text-xs text-gray-500">{fiabiliteKpi.mismatch_partenaire.taux}% chauffeurs</p>
                      </div>
                      <div className="p-4 rounded-lg bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Total Chauffeurs</span>
                          <span className="text-2xl font-bold text-gray-900">{fiabiliteKpi.total_chauffeurs}</span>
                        </div>
                        <p className="text-xs text-gray-500">Base totale</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-md border-0">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Users className="h-5 w-5 text-gray-600" />
                      Inactifs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className={`p-4 rounded-lg ${
                        fiabiliteKpi.chauffeurs_fantomes.status === 'ok' ? 'bg-green-50' : 'bg-orange-50'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Chauffeurs Fantômes</span>
                          <span className="text-2xl font-bold text-orange-600">{fiabiliteKpi.chauffeurs_fantomes.nombre}</span>
                        </div>
                        <p className="text-xs text-gray-500">{fiabiliteKpi.chauffeurs_fantomes.taux}% sans opération</p>
                      </div>
                      <div className={`p-4 rounded-lg ${
                        fiabiliteKpi.vehicules_dormants.status === 'ok' ? 'bg-green-50' : 'bg-orange-50'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Véhicules Dormants</span>
                          <span className="text-2xl font-bold text-orange-600">{fiabiliteKpi.vehicules_dormants.nombre}</span>
                        </div>
                        <p className="text-xs text-gray-500">{fiabiliteKpi.vehicules_dormants.taux}% inutilisés</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Section Marchandises */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">📦 Analyse des Marchandises</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Top Marchandises */}
                  <Card className="shadow-md border-0">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Package className="h-5 w-5 text-emerald-600" />
                        Top 5 Marchandises
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {marchandisesKpi.top_marchandises && marchandisesKpi.top_marchandises.length > 0 ? (
                        <div className="space-y-3">
                          {marchandisesKpi.top_marchandises.map((item, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                              <div className="flex items-center gap-3">
                                <div className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-white ${
                                  index === 0 ? 'bg-yellow-500' :
                                  index === 1 ? 'bg-gray-400' :
                                  index === 2 ? 'bg-orange-600' : 'bg-gray-300'
                                }`}>
                                  {index + 1}
                                </div>
                                <span className="text-sm font-medium text-gray-700">{item.type}</span>
                              </div>
                              <span className="text-lg font-bold text-emerald-600">{item.count}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Types de Conteneurs */}
                  <Card className="shadow-md border-0">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Truck className="h-5 w-5 text-blue-600" />
                        Types de Conteneurs
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {marchandisesKpi.types_conteneurs && marchandisesKpi.types_conteneurs.length > 0 ? (
                        <div className="space-y-3">
                          {marchandisesKpi.types_conteneurs.map((item, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                              <span className="text-sm font-medium text-gray-700">{item.type}</span>
                              <span className="text-lg font-bold text-blue-600">{item.count}</span>
                            </div>
                          ))}
                          <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                            <p className="text-sm text-gray-700">
                              <strong>Dominant :</strong> {marchandisesKpi.type_conteneur_dominant || 'N/A'}
                            </p>
                            <p className="text-xs text-gray-600 mt-1">Total : {marchandisesKpi.nb_conteneurs} conteneurs</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-8">Aucun conteneur</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Volumes et Poids */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                  <Card className="shadow-md border-0 bg-gradient-to-br from-blue-50 to-cyan-50">
                    <CardContent className="p-6 text-center">
                      <p className="text-sm text-gray-600 mb-2">Volume Total</p>
                      <p className="text-3xl font-bold text-blue-600">{marchandisesKpi.volume_total_m3}</p>
                      <p className="text-xs text-gray-500 mt-1">m³</p>
                    </CardContent>
                  </Card>
                  <Card className="shadow-md border-0 bg-gradient-to-br from-green-50 to-emerald-50">
                    <CardContent className="p-6 text-center">
                      <p className="text-sm text-gray-600 mb-2">Poids Total</p>
                      <p className="text-3xl font-bold text-green-600">{marchandisesKpi.poids_total_tonnes}</p>
                      <p className="text-xs text-gray-500 mt-1">tonnes</p>
                    </CardContent>
                  </Card>
                  <Card className="shadow-md border-0 bg-gradient-to-br from-purple-50 to-pink-50">
                    <CardContent className="p-6 text-center">
                      <p className="text-sm text-gray-600 mb-2">Volume Moyen</p>
                      <p className="text-3xl font-bold text-purple-600">{marchandisesKpi.volume_moyen_m3}</p>
                      <p className="text-xs text-gray-500 mt-1">m³/op</p>
                    </CardContent>
                  </Card>
                  <Card className="shadow-md border-0 bg-gradient-to-br from-amber-50 to-yellow-50">
                    <CardContent className="p-6 text-center">
                      <p className="text-sm text-gray-600 mb-2">Poids Moyen</p>
                      <p className="text-3xl font-bold text-amber-600">{marchandisesKpi.poids_moyen_tonnes}</p>
                      <p className="text-xs text-gray-500 mt-1">t/op</p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Footer */}
              <Card className="border-0 bg-gray-50">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-sm text-gray-600">
                    <span>📊 Période marchandises : <strong>{marchandisesKpi.period_days} jours</strong></span>
                    <span>🔍 Analyse fiabilité : <strong>Base complète</strong></span>
                    <span>📅 Dernière mise à jour : {new Date().toLocaleString('fr-FR')}</span>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Chargement des KPI fiabilité & marchandises...</p>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ============================================
            ONGLET CROISSANCE & PERFORMANCE PLATEFORME
        ============================================ */}
        <TabsContent value="croissance" className="space-y-6 mt-6">
          {/* Sélecteur de période */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gray-50 p-4 rounded-lg">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">KPI Croissance & Performance</h2>
              <p className="text-sm text-gray-600 mt-1">Évolution du réseau et maturité digitale</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant={periodDays === 7 ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriodDays(7)}
              >
                7 jours
              </Button>
              <Button
                variant={periodDays === 30 ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriodDays(30)}
              >
                30 jours
              </Button>
              <Button
                variant={periodDays === 90 ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriodDays(90)}
              >
                90 jours
              </Button>
            </div>
          </div>

          {growthKpi ? (
            <>
              {/* KPI Cards Grid - Croissance Mensuelle */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">📈 Croissance Mensuelle</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Opérations ce Mois */}
                  <KPICard
                    title="Opérations ce Mois"
                    value={growthKpi.operations_ce_mois || 0}
                    icon={Package}
                    color="bg-gradient-to-br from-blue-50 to-indigo-50"
                  />

                  {/* Opérations Mois Précédent */}
                  <KPICard
                    title="Opérations Mois Précédent"
                    value={growthKpi.operations_mois_precedent || 0}
                    icon={Activity}
                    color="bg-gradient-to-br from-teal-50 to-cyan-50"
                  />

                  {/* Croissance MoM */}
                  <Card className="border-0 shadow-md bg-gradient-to-br from-green-50 to-emerald-50">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-700 mb-2">Croissance MoM</p>
                          <p className={`text-3xl font-bold ${growthKpi.croissance_mom_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {growthKpi.croissance_mom_pct >= 0 ? '+' : ''}{growthKpi.croissance_mom_pct || 0}%
                          </p>
                          <p className="text-xs text-gray-600 mt-1">mois sur mois</p>
                        </div>
                        <div className="p-3 rounded-xl bg-white bg-opacity-50">
                          {growthKpi.croissance_mom_pct >= 0 ? (
                            <TrendingUp className="h-6 w-6 text-green-600" />
                          ) : (
                            <TrendingDown className="h-6 w-6 text-red-600" />
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Total Users */}
                  <KPICard
                    title="Utilisateurs Plateforme"
                    value={growthKpi.total_users || 0}
                    icon={Users}
                    color="bg-gradient-to-br from-purple-50 to-pink-50"
                  />
                </div>
              </div>

              {/* KPI Nouveaux Acteurs */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Nouveaux Chauffeurs */}
                <Card className="shadow-md border-0 bg-gradient-to-br from-purple-50 to-pink-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Users className="h-5 w-5 text-purple-600" />
                      Nouveaux Chauffeurs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-6">
                      <div className="text-5xl font-bold text-purple-600 mb-2">
                        {growthKpi.nouveaux_chauffeurs_mois || 0}
                      </div>
                      <p className="text-sm text-gray-600">Ce mois</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Nouveaux Clients */}
                <Card className="shadow-md border-0 bg-gradient-to-br from-green-50 to-emerald-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Users className="h-5 w-5 text-green-600" />
                      Nouveaux Clients
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-6">
                      <div className="text-5xl font-bold text-green-600 mb-2">
                        {growthKpi.nouveaux_clients_mois || 0}
                      </div>
                      <p className="text-sm text-gray-600">Ce mois</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Utilisateurs Actifs */}
                <Card className="shadow-md border-0 bg-gradient-to-br from-blue-50 to-indigo-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Activity className="h-5 w-5 text-blue-600" />
                      Engagement Plateforme
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-white bg-opacity-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Total utilisateurs</span>
                        <span className="text-2xl font-bold text-blue-600">
                          {growthKpi.total_users || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white bg-opacity-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Actifs (30j)</span>
                        <span className="text-2xl font-bold text-green-600">
                          {growthKpi.users_actifs_30j || 0}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Graphique de Performance */}
              <Card className="shadow-md border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-emerald-600" />
                    Résumé de Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Nouveaux Partenaires</p>
                      <p className="text-3xl font-bold text-blue-600">{growthKpi.nouveaux_partenaires || 0}</p>
                      <div className={`text-xs mt-2 ${
                        growthKpi.targets?.nouveaux_partenaires?.status === 'ok' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        Objectif: {growthKpi.targets?.nouveaux_partenaires?.value}
                      </div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Nouveaux Clients</p>
                      <p className="text-3xl font-bold text-green-600">{growthKpi.nouveaux_clients || 0}</p>
                      <div className={`text-xs mt-2 ${
                        growthKpi.targets?.nouveaux_clients?.status === 'ok' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        Objectif: {growthKpi.targets?.nouveaux_clients?.value}
                      </div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Nouveaux Chauffeurs</p>
                      <p className="text-3xl font-bold text-purple-600">{growthKpi.nouveaux_chauffeurs || 0}</p>
                      <p className="text-xs text-gray-500 mt-2">Période: {growthKpi.period_days}j</p>
                    </div>
                    <div className="text-center p-4 bg-amber-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Croissance</p>
                      <p className={`text-3xl font-bold ${
                        growthKpi.taux_croissance_operations !== null && growthKpi.taux_croissance_operations >= 0 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {growthKpi.taux_croissance_operations !== null 
                          ? `${growthKpi.taux_croissance_operations?.toFixed(1)}%` 
                          : 'N/A'}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        {growthKpi.taux_croissance_operations !== null ? 'vs période précédente' : 'Nécessite ≥60j'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Footer Info */}
              <Card className="border-0 bg-gray-50">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-sm text-gray-600">
                    <span>📅 Période d'analyse : <strong>{growthKpi.period_days} jours</strong></span>
                    <span>💡 Conseil : Utilisez 90j pour voir la croissance des opérations</span>
                    <span>📊 Dernière mise à jour : {new Date().toLocaleString('fr-FR')}</span>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Chargement des KPI croissance...</p>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;
